/**
 * useVisualViewportInset
 *
 * Tracks the on-screen keyboard via the Visual Viewport API and publishes it as
 * the `--kb-inset` CSS custom property (px) on `<html>`.
 *
 * Why both this and `interactive-widget=resizes-content` in the viewport meta:
 * - Chromium 108+ honours the meta tag and shrinks the *layout* viewport when
 *   the keyboard opens, so `h-dvh` layouts already fit; the inset stays 0.
 * - iOS Safari ignores it: the keyboard overlays the layout viewport and only
 *   the *visual* viewport shrinks. The inset (layout height minus visual
 *   height) is the amount the keyboard covers, so a fixed/pinned composer can
 *   lift itself with `bottom: var(--kb-inset, 0px)` / `padding-bottom`.
 *
 * `keyboardOpen` additionally requires an editable element to be focused, so a
 * collapsing browser toolbar or a split-screen resize is never mistaken for a
 * keyboard. The heuristic "viewport shrank by more than `threshold` px versus
 * the tallest viewport seen at this width" works for both behaviours above.
 */
import { useEffect, useState } from "react";

export const KEYBOARD_THRESHOLD_PX = 150;
export const KEYBOARD_INSET_CSS_VAR = "--kb-inset";

export interface VisualViewportInsetState {
  /** Pixels of the layout viewport covered by the keyboard (0 when closed). */
  inset: number;
  /** True while an on-screen keyboard is (very likely) open. */
  keyboardOpen: boolean;
}

const CLOSED: VisualViewportInsetState = { inset: 0, keyboardOpen: false };

function isEditable(element: Element | null): boolean {
  if (!element) return false;
  const tag = element.tagName;
  if (tag === "TEXTAREA" || tag === "SELECT") return true;
  if (tag === "INPUT") {
    const type = (element as HTMLInputElement).type;
    return !["button", "checkbox", "radio", "range", "submit", "reset", "file", "image"].includes(type);
  }
  return (element as HTMLElement).isContentEditable === true;
}

export function useVisualViewportInset(
  threshold: number = KEYBOARD_THRESHOLD_PX,
): VisualViewportInsetState {
  const [state, setState] = useState<VisualViewportInsetState>(CLOSED);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const vv = window.visualViewport;
    if (!vv) return undefined;

    const root = document.documentElement;
    let baseline = vv.height + vv.offsetTop;
    let baselineWidth = vv.width;

    const update = () => {
      // A width change is a rotation / split-screen resize, not a keyboard.
      if (vv.width !== baselineWidth) {
        baselineWidth = vv.width;
        baseline = vv.height + vv.offsetTop;
      }
      const visibleBottom = vv.height + vv.offsetTop;
      if (visibleBottom > baseline) baseline = visibleBottom;

      // `overlay`: how much of the layout viewport the keyboard covers (0 when
      // the browser already resized the layout viewport for us).
      const overlay = Math.max(0, Math.round(window.innerHeight - visibleBottom));
      // `shrink`: how much shorter the viewport is than at rest at this width.
      const shrink = Math.max(0, Math.round(baseline - visibleBottom));
      const open = shrink > threshold && isEditable(document.activeElement);
      const inset = open ? overlay : 0;

      root.style.setProperty(KEYBOARD_INSET_CSS_VAR, `${inset}px`);
      setState((prev) =>
        prev.inset === inset && prev.keyboardOpen === open
          ? prev
          : { inset, keyboardOpen: open },
      );
    };

    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    document.addEventListener("focusin", update);
    document.addEventListener("focusout", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
      document.removeEventListener("focusin", update);
      document.removeEventListener("focusout", update);
      root.style.removeProperty(KEYBOARD_INSET_CSS_VAR);
    };
  }, [threshold]);

  return state;
}
